import { CartComponentBase } from '@utilities/cart-component-base';
import { publish, EVENTS } from '@utilities/events';
import { handleize } from '@utilities/theme';

/**
 * Cart Items Component, manages the state of the cart items container
 * @class CartItems
 * @extends CartComponentBase
 */
class CartItems extends CartComponentBase {
  constructor() {
    super();

    /** @type {cart-drawer} Reference to parent cart drawer */
    this.cartDrawer = this.closest("cart-drawer");

    /** @type {Map<string, cart-item>} Map of item keys to cart-item elements */
    this.itemComponents = new Map();

    /** @type {Object} Component configuration */
    this.config = null;

    /** @type {boolean} Whether we're in initial load mode */
    this.isInitialLoad = true;

    // Bind event handlers
    this.handleStateChange = this.handleStateChange.bind(this);
  }

  /**
   * Get cart state from state manager
   * @returns {Object|null} Current cart state
   * @private
   */
  get cartState() {
    return this.getCartState();
  }

  /**
   * Handle cart state changes
   * @param {CustomEvent} event - The cart state change event
   */
  async handleStateChange(data) {
    const { state, changes } = data;

    // If no changes provided or changes is empty, this might be a refresh without actual changes
    const hasChanges = changes && Object.keys(changes).length > 0;

    if (this.allowDebug()) {
      console.log("CartItems: Received cart-state:changed event:", {
        hasState: !!state,
        hasChanges: hasChanges,
        itemCount: state?.itemCount || 0,
        changes: changes,
        currentItemCount: this.itemComponents.size,
        source: data?.source || 'unknown',
        forced: data?.forced || false
      });
    }

    // Check if items changed
    const itemsChanged =
      hasChanges &&
      (changes.itemsAdded ||
        changes.itemsRemoved ||
        changes.itemsUpdated);

    // Check if we need to sync (items exist but we have no components)
    const needsSync = state?.items?.length > 0 && this.itemComponents.size === 0;

    if (this.allowDebug()) {
      console.log("CartItems: Evaluating update conditions", {
        itemsChanged,
        needsSync,
        stateItemCount: state?.items?.length || 0,
        componentCount: this.itemComponents.size,
        hasChanges,
        changes: changes
      });
    }

    if (itemsChanged || needsSync) {
      if (this.allowDebug()) {
        console.log("CartItems: Update needed", {
          reason: itemsChanged ? "items changed" : "sync needed",
          itemsChanged,
          needsSync
        });
      }
      // Items changed or we have items but no components (likely after template switch)
      await this.#updateItems(state, changes);
    } else if (this.allowDebug()) {
      console.log("CartItems: No item changes detected, skipping update");
    }

    // Notify child cart-item components of state update
    this.#dispatchStateUpdateForItems(state);
  }

  /**
   * Update cart items based on state changes
   * @param {Object} state - Current cart state
   * @param {Object} changes - What changed
   * @private
   */
  async #updateItems(state, changes) {
    if (this.allowDebug()) {
      console.log("CartItems: Updating items", {
        newItemCount: state.items?.length || 0,
        changes,
      });
    }

    // Get current cart state
    const currentState = this.cartState;
    const hasState = !!currentState;

    if (this.allowDebug()) {
      console.log("CartItems: Using cart state from state manager during update", {
        hasState,
        itemCount: currentState?.items?.length || 0
      });
    }

    // Get current cart item keys
    const currentItemKeys = new Set(
      state.items?.map((item) => item.key) || []
    );
    const existingItemKeys = new Set(this.itemComponents.keys());

    // Remove items that are no longer in cart
    for (const existingKey of existingItemKeys) {
      if (!currentItemKeys.has(existingKey)) {
        await this.#removeItem(existingKey);
      }
    }

    // Add or update items that are in cart
    for (const item of state.items || []) {
      if (this.itemComponents.has(item.key)) {
        // Item exists - it will update itself via its own state change listener
        continue;
      } else {
        // New item - create component
        await this.#addItem(item);
      }
    }
  }

  /**
   * Add a new cart item component
   * @param {Object} item - Cart item data
   * @private
   */
  async #addItem(item) {
    if (this.allowDebug()) {
      console.log("CartItems: Adding item component", {
        key: item.key,
        title: item.title,
        isInitialLoad: this.isInitialLoad,
      });
    }

    // Get full item data from state if available
    const itemData =
      this.cartState?.items?.find(
        (stateItem) => stateItem.key === item.key
      ) || item;

    // Safari-specific defensive check: ensure required properties exist
    const safeItemData = {
      ...itemData,
      priceHtml: itemData.priceHtml || item.priceHtml || `<span class="price"><span class="price-item">Loading...</span></span>`,
      featuredMedia: itemData.featuredMedia || item.featuredMedia || item.image || null,
      title: itemData.title || item.title || item.productTitle || 'Product',
      url: itemData.url || item.url || '#'
    };

    if (this.allowDebug()) {
      console.log("CartItems: Creating cart item", {
        key: item.key,
        title: item.title,
        hasState: !!this.cartState,
        hasFullData: !!itemData.priceHtml, // Check if we have full data
        stateItemKeys: this.cartState?.items?.map(i => i.key) || []
      });
    }

    // Create cart-item element
    const cartItemElement = this.#createCartItemElement(safeItemData);

    // Add to DOM based on whether this is initial load or dynamic update
    if (this.isInitialLoad) {
      // During initial load, append items to maintain config order
      // (config.items is already ordered newest-first from Shopify)
      this.appendChild(cartItemElement);
    } else {
      // During dynamic updates, prepend new items to show them at the top
      // (new items from cart updates appear at start of Shopify's items array)
      this.prepend(cartItemElement);
    }

    // Track the component
    this.itemComponents.set(item.key, cartItemElement);
  }

  /**
   * Remove a cart item component
   * @param {string} itemKey - Item key to remove
   * @private
   */
  async #removeItem(itemKey) {
    const itemComponent = this.itemComponents.get(itemKey);

    if (itemComponent) {
      if (this.allowDebug()) {
        console.log("CartItems: Removing item component", { key: itemKey });
      }

      // Add removal animation
      itemComponent.classList.add("cart-item--removing");

      // Remove after animation
      setTimeout(() => {
        if (itemComponent.parentNode) {
          itemComponent.remove();
        }
      }, 300);

      // Remove from tracking
      this.itemComponents.delete(itemKey);
    }
  }

  /**
   * Create a cart-item element with configuration data
   * @param {Object} itemData - Item data from state
   * @returns {HTMLElement} cart-item element
   * @private
   */
  #createCartItemElement(itemData) {
    const cartItem = document.createElement("cart-item");
    cartItem.classList.add("cart-item");
    if (this.allowDebug()) {
      cartItem.classList.add("debug");
    }
    if (itemData.isHidden) {
      cartItem.classList.add("hide");
    }
    cartItem.id = `cart-item--${handleize(
      itemData.key
    )}`;

    // Create configuration script with comprehensive data
    const configData = {
      key: itemData.key,
      quantity: itemData.quantity,
      handle: itemData.handle,
      title: itemData.title,
      productTitle: itemData.productTitle,
      variantTitle: itemData.variantTitle,
      variantId: itemData.variantId,
      featuredMedia: itemData.featuredMedia,
      available: itemData.available,
      productId: itemData.productId,
      colorId: itemData.colorId || "",
      hasItemComponents: itemData.hasItemComponents || false,
      itemComponents: itemData.itemComponents || [],
      ...(itemData.bundleId && { bundleId: itemData.bundleId }),
      isFinalSale: itemData.isFinalSale || false,
      isBundleMain: itemData.isBundleMain || false,
      isLoyaltyBundle: itemData.isLoyaltyBundle || false,
      isBundlePiece: itemData.isBundlePiece || false,
      hasPreorderMessage: itemData.hasPreorderMessage || false,
      preorderMessage: itemData.preorderMessage || "",
      hasMinicartMessage: itemData.hasMinicartMessage || false,
      minicartMessage: itemData.minicartMessage || "",
      isCheckoutBlocking: itemData.isCheckoutBlocking || false,
      isGift: itemData.isGift || false,
      isGiftCard: itemData.isGiftCard || false,
      isCyoProduct: itemData.isCyoProduct || false,
      isCyoHat: itemData.isCyoHat || false,
      isOnSale: itemData.isOnSale || false,
      compareAtPrice: itemData.compareAtPrice || 0,
      compareAtLinePrice: itemData.compareAtLinePrice || 0,
      compareAtPriceDiscount: itemData.compareAtPriceSavings || 0,
      compareAtLinePriceDiscount: itemData.compareAtLinePriceSavings || 0,
      compareAtLinePriceDiscountMoney: itemData.compareAtLinePriceSavingsMoney || "$0",
      variantPrice: itemData.variantPrice || 0,
      lineItemCombinedTotalDiscount: itemData.lineItemCombinedTotalDiscount || 0,
      lineItemCombinedTotalDiscountMoney: itemData.lineItemCombinedTotalDiscountMoney || "$0",
      lineLevelTotalDiscount: itemData.lineLevelTotalDiscount || 0,
      lineLevelTotalDiscountMoney: itemData.lineLevelTotalDiscountMoney || "$0",
      disableQuantity: itemData.disableQuantity || false,
      disableDiscountMessaging: itemData.disableDiscountMessaging || false,
      disableRemove: itemData.disableRemove || false,
      message: itemData.message || "",
      discountAllocations: itemData.discountAllocations || [],
      originalLinePrice: itemData.originalLinePrice,
      finalLinePrice: itemData.finalLinePrice,
      finalLinePriceMoney: itemData.finalLinePriceMoney || "$0",
      inventoryQuantity: itemData.inventoryQuantity || null,
      inventoryPolicy: itemData.inventoryPolicy || null,
      url: itemData.url,
      priceHtml: itemData.priceHtml,
      properties: itemData.properties || {},
      promotionMessage: itemData.promotionMessage || "",
      hasPromotionMessage: itemData.hasPromotionMessage || false
    };

    if (this.allowDebug()) {
      console.log("CartItems: Creating cart item config", {
        key: itemData.key,
        hasPriceHtml: !!itemData.priceHtml,
        hasDiscountAllocations: !!(itemData.discountAllocations?.length),
        hasOriginalPrice: !!itemData.originalLinePrice,
        configDataKeys: Object.keys(configData)
      });
    }

    const configScript = document.createElement("script");
    configScript.type = "application/json";
    configScript.id = `${cartItem.id}__json`;
    configScript.setAttribute("component-config", "");
    configScript.textContent = JSON.stringify(configData);

    // Append the config script - CartItem will handle its own rendering
    cartItem.appendChild(configScript);

    if (this.allowDebug()) {
      console.log("CartItems: Created cart item with A/B test version:", {
        itemKey: itemData.key,
        itemId: cartItem.id,
      });
    }

    return cartItem;
  }

  /**
   * Override the base class component-specific initialization
   * @protected
   */
  async _initializeComponent() {
    if (this.allowDebug()) {
      console.log("CartItems: Starting initialization", {
        hasConfig: !!this.config,
        configItemCount: this.config?.items?.length || 0,
        isConnected: this.isConnected,
        id: this.id
      });
    }

    let stateManager;
    try {
      // Wait for cart state manager to be ready
      stateManager = await this.waitForCartStateManager();
    } catch (error) {
      if (this.allowDebug()) {
        console.warn('CartItems: Cart state manager not ready, skipping initialization');
      }
      return;
    }

    const currentState = stateManager.getState();

    if (this.allowDebug()) {
      console.log("CartItems: Initializing with current state", {
        hasConfig: !!this.config,
        configItemCount: this.config?.items?.length || 0,
        currentStateItemCount: currentState?.items?.length || 0,
        willUseCurrentState: !!(currentState?.items?.length > 0),
      });
    }

    // If we have current state with items, use that instead of stale config
    if (currentState?.items?.length > 0) {
      if (this.allowDebug()) {
        console.log("CartItems: Using current state with", currentState.items.length, "items");
      }

      // Get cart state from state manager during initialization
      const currentState = this.cartState;
      if (this.allowDebug()) {
        console.log("CartItems: Using cart state from state manager during initialization", {
          hasState: !!currentState,
          itemCount: currentState?.items?.length || 0
        });
      }

      // Create items from current state
      for (const item of currentState.items) {
        await this.#addItem(item);
      }

      if (this.allowDebug()) {
        console.log("CartItems: Initialized with current state", {
          itemCount: currentState.items.length,
          componentCount: this.itemComponents.size,
          hasState: !!this.cartState
        });
      }
    } else if (this.config && this.config.items?.length > 0) {
      // Fallback to config data if no current state (shouldn't happen in normal flow)
      // Note: state contains all UI properties

      if (this.allowDebug()) {
        console.log("CartItems: Falling back to config data", {
          itemCount: this.config.items.length,
        });
      }

      // Create initial cart items from config
      for (const item of this.config.items) {
        await this.#addItem(item);
      }
    } else {
      if (this.allowDebug()) {
        console.log("CartItems: No items to initialize - empty state", {
          currentStateItemCount: currentState?.items?.length || 0,
          configItemCount: this.config?.items?.length || 0
        });
      }
    }

    // Mark initial load as complete - all subsequent additions will be dynamic
    this.isInitialLoad = false;

    if (this.allowDebug()) {
      console.log("CartItems: Initialization complete", {
        itemComponentCount: this.itemComponents.size,
        isInitialLoad: this.isInitialLoad,
        hasState: !!this.cartState
      });
    }
  }

  /**
   * Override the base class event listener setup
   * @protected
   */
  _setupEventListeners() {
    if (this.allowDebug()) {
      console.log("CartItems: Setting up event listeners", {
        id: this.id,
        isConnected: this.isConnected
      });
    }

    this._subscribe(EVENTS.CART_STATE_CHANGED, this.handleStateChange);
    this._subscribe(EVENTS.CART_STATE_REFRESHED, this.#handleStateRefreshed.bind(this));
    this._subscribe(EVENTS.CART_ITEM_BUNDLE_REMOVE, this.#handleBundleRemove.bind(this));
    this._subscribe(EVENTS.CART_ITEM_BUNDLE_UPDATE, this.#handleBundleUpdate.bind(this));

    if (this.allowDebug()) {
      console.log("CartItems: Event listeners set up successfully");
    }
  }

  /**
   * Handle bundle removal requests from cart items
   *
   * MEMBERSHIP BUNDLE REMOVAL:
   * This handler processes requests to remove entire bundles, particularly important
   * for membership bundles where ALL items must be removed together as a cohesive unit.
   *
   * When a membership bundle is removed:
   * 1. All items with matching bundleId are identified and removed
   * 2. A special "cart:membership-bundle:removed" event is dispatched for UI updates
   * 3. The membership minilanding component listens for this event to reload the page
   *
   * Bundle identification:
   * - Uses state data when available (item.bundleId)
   * - Falls back to properties parsing (_gc:bundle-ID) for basic cart data
   *
   * @param {Object} data - Bundle remove data with bundleId, isLoyaltyBundle, membershipHandle
   * @private
   */
  async #handleBundleRemove(data) {
    const { bundleId, isLoyaltyBundle, membershipHandle } = data;

    if (this.allowDebug()) {
      console.log("CartItems: Handling bundle removal request", {
        bundleId,
        isLoyaltyBundle,
        membershipHandle,
      });
    }

    try {
      await this.#removeBundleItems(bundleId);

      // Dispatch membership bundle event if needed
      // This event triggers page reload in membership-minilanding.js to update membership status
      if (isLoyaltyBundle && membershipHandle) {
        publish(EVENTS.CART_MEMBERSHIP_BUNDLE_REMOVED, { membership: membershipHandle });
      }

      publish(EVENTS.CART_ITEM_BUNDLE_OPERATION_COMPLETE, { bundleId, operation: "remove", success: true });
    } catch (error) {
      this._handleError(error, "removing bundle items");

      publish(EVENTS.CART_ITEM_BUNDLE_OPERATION_COMPLETE, {
        bundleId,
        operation: "remove",
        success: false,
        error: error.message,
      });
    }
  }

  /**
   * Handle bundle quantity update requests from cart items
   * @param {Object} data - Bundle update data
   * @private
   */
  async #handleBundleUpdate(data) {
    const { bundleId, newQuantity } = data;

    if (this.allowDebug()) {
      console.log("CartItems: Handling bundle update request", {
        bundleId,
        newQuantity,
      });
    }

    try {
      await this.#updateBundleQuantities(bundleId, newQuantity);

      publish(EVENTS.CART_ITEM_BUNDLE_OPERATION_COMPLETE, { bundleId, operation: "update", success: true });
    } catch (error) {
      this._handleError(error, "updating bundle quantities");

      publish(EVENTS.CART_ITEM_BUNDLE_OPERATION_COMPLETE, {
        bundleId,
        operation: "update",
        success: false,
        error: error.message,
      });
    }
  }

  /**
   * Remove all items in a bundle
   *
   * BUNDLE ITEM IDENTIFICATION:
   * This method handles both state and basic cart data to identify bundle items:
   *
   * State data (preferred):
   * - Uses item.bundleId property directly
   * - More reliable and performant
   *
   * Basic cart data (fallback):
   * - Parses item.properties array/object for "_gc:bundle-ID"
   * - Handles both array format: [["_gc:bundle-ID", "bundle-123"]]
   * - And object format: {"_gc:bundle-ID": "bundle-123"}
   *
   * BUNDLE TYPES:
   *
   * A. Membership bundles - all items share the same bundleId:
   * Example: "bundle-440242872832" includes:
   * - Black Wolf Membership (main item)
   * - Wolf Headcover (included item)
   * - Putter Cover (included item)
   * - Snapback (included item)
   *
   * B. Product bundles - created by ProductBundleOverview/ProductBundleItem:
   * Example: "bundle-product-overview-123-timestamp" includes:
   * - Multiple coordinated items that can be removed independently
   * - Uses properties: _gc:bundle-product, _gc:bundle-length, _gc:bundle-main
   *
   * @param {string} bundleId - Bundle ID to remove (e.g., "bundle-440242872832")
   * @private
   */
  async #removeBundleItems(bundleId) {
    if (this.allowDebug()) {
      console.log(
        "CartItems: Removing bundled items for bundle ID:",
        bundleId
      );
    }

    try {
      const currentState = this.cartStateManager?.getState();
      const bundleItemsToRemove = [];

      // Use cart state items
      const itemsToCheck = this.cartState?.items || currentState.items || [];

      if (this.allowDebug()) {
        console.log("CartItems: Checking items for bundle removal", {
          itemCount: itemsToCheck.length,
          bundleId,
          usingState: !!this.cartState,
        });
      }

      // Find all items that belong to this bundle
      itemsToCheck.forEach((item) => {
        let itemBundleId = null;

        if (this.cartState) {
          // Use state bundleId
          itemBundleId = item.bundleId;
        } else {
          // Extract bundleId from properties for basic cart data (fallback method)
          if (item.properties && Array.isArray(item.properties)) {
            // Handle array format: [["_gc:bundle-ID", "bundle-123"], ...]
            const bundleIdProperty = item.properties.find(
              (prop) => Array.isArray(prop) && prop[0] === "_gc:bundle-ID"
            );
            itemBundleId = bundleIdProperty ? bundleIdProperty[1] : null;
          } else if (item.properties && typeof item.properties === "object") {
            // Handle object format: {"_gc:bundle-ID": "bundle-123", ...}
            itemBundleId = item.properties["_gc:bundle-ID"] || null;
          }
        }

        if (itemBundleId === bundleId) {
          bundleItemsToRemove.push(item);
        }
      });

      if (bundleItemsToRemove.length === 0) {
        if (this.allowDebug()) {
          console.log("CartItems: No bundle items found to remove for bundle:", bundleId);
        }
        return;
      }

      if (this.allowDebug()) {
        console.log(
          "CartItems: Found bundle items to remove:",
          bundleItemsToRemove.length
        );
        bundleItemsToRemove.forEach((item) => {
          console.log("- Will remove:", item.key, item.title || item.productTitle);
        });
      }

      // Extract just the keys for bulk removal
      const keysToRemove = bundleItemsToRemove.map((item) => item.key);

      // Remove all bundle items in a single request via CartStateManager
      await this.cartStateManager?.removeItems(keysToRemove);

      if (this.allowDebug()) {
        console.log("CartItems: Bundle items removed successfully");
      }
    } catch (error) {
      this._handleError(error, "removing bundle items");
      throw error;
    }
  }

  /**
   * Update quantities for all items in a bundle
   * @param {string} bundleId - Bundle ID to update
   * @param {number} newQuantity - New quantity for all bundle items
   * @private
   */
  async #updateBundleQuantities(bundleId, newQuantity) {
    if (this.allowDebug()) {
      console.log("CartItems: Updating bundle quantities", {
        bundleId,
        newQuantity,
      });
    }

    try {
      const currentState = this.cartStateManager?.getState();
      const bundleItemsToUpdate = [];

      // Use cart state items
      const itemsToCheck = this.cartState?.items || currentState.items || [];

      // Find all items that belong to this bundle
      itemsToCheck.forEach((item) => {
        let itemBundleId = null;

        if (this.cartState) {
          // Use state bundleId
          itemBundleId = item.bundleId;
        } else {
          // Extract bundleId from properties for basic cart data
          if (item.properties && Array.isArray(item.properties)) {
            const bundleIdProperty = item.properties.find(
              (prop) => Array.isArray(prop) && prop[0] === "_gc:bundle-ID"
            );
            itemBundleId = bundleIdProperty ? bundleIdProperty[1] : null;
          } else if (item.properties && typeof item.properties === "object") {
            itemBundleId = item.properties["_gc:bundle-ID"] || null;
          }
        }

        if (itemBundleId === bundleId) {
          bundleItemsToUpdate.push(item);
        }
      });

      if (bundleItemsToUpdate.length === 0) {
        if (this.allowDebug()) {
          console.log("CartItems: No bundle items found to update");
        }
        return;
      }

      if (this.allowDebug()) {
        console.log(
          "CartItems: Found bundle items to update:",
          bundleItemsToUpdate.length
        );
      }

      // Update all bundle items
      const updatePromises = bundleItemsToUpdate.map((item) =>
        this.cartStateManager?.updateItem(item.key, newQuantity)
      );

      await Promise.all(updatePromises);

      if (this.allowDebug()) {
        console.log("CartItems: Bundle quantities updated successfully");
      }
    } catch (error) {
      this._handleError(error, "updating bundle quantities");
      throw error;
    }
  }

  /**
   * Dispatch state update event for child cart-item components
   * @param {Object} state - Current cart state
   * @private
   */
  #dispatchStateUpdateForItems(state) {
    if (state) {
      publish(EVENTS.CART_ITEMS_STATE_UPDATED, { state });
    }
  }

  /**
   * Handle cart state refreshed events (including visibility changes)
   *
   * On refresh events, we need to force a full re-sync of all cart items
   * because handleStateChange only updates if it detects specific changes.
   * A refresh may not have "changes" but still needs all cart-item components
   * to be re-rendered to ensure they're in sync with current state.
   *
   * @param {Object} data - The cart state refreshed data
   * @private
   */
  async #handleStateRefreshed(data) {
    const { state, reason } = data;

    if (this.allowDebug()) {
      console.log("CartItems: Received cart-state:refreshed event", {
        reason,
        currentItemCount: this.itemComponents.size,
        stateItemCount: state?.items?.length || 0
      });
    }

    // Force a full re-sync by calling updateItems with the current state
    // This ensures all cart-item components are re-created/updated regardless
    // of whether there were detected changes
    if (state) {
      await this.#updateItems(state, {
        forced: true,
        reason: 'state-refreshed'
      });
    }
  }


}

if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}

export default CartItems;
